<template>
  <div class="bg p-3 pb-20 mb-auto h-full overflow-auto text-white" id="style-1">
    <!-- Input and Selection Section -->
    <div class="text-center mb-6">
      <h2 class="text-lg sm:text-xl font-semibold text-indigo-300 mb-3 font-orbitron animate-fadeIn">
        ตรวจสอบน้ำหนักไอเทมในคลังและกระเป๋า
      </h2>

      <div class="flex flex-col sm:flex-row justify-center items-center gap-3">
        <div class="flex items-center gap-3">
          <input
            v-model="uid"
            type="text"
            placeholder="กรอก UID ผู้เล่น"
            class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
          />

          <button
            @click="fetchInventoryData"
            :disabled="isLoading"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isLoading }"
          >
            <div class="btn-content">
              <i class="fas fa-search text-base"></i>
              <span class="text-xs font-semibold">ค้นหา</span>
            </div>
          </button>
        </div>

        <div class="flex items-center gap-3">
          <select
            v-model="selectedCharacter"
            class="bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 w-64"
            @change="fetchInventoryDataByCharacter"
          >
            <option value="">เลือกตัวละครจากอันดับ</option>
            <option v-for="(survivor, index) in survivors" :key="survivor.docId" :value="survivor.docId">
              #{{ index + 1 }} {{ survivor.characterName || 'ไม่ทราบ' }}
            </option>
          </select>

          <button
            @click="previousCharacter"
            :disabled="isLoading || currentIndex <= 0"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isLoading || currentIndex <= 0 }"
          >
            <div class="btn-content">
              <i class="fas fa-arrow-left text-base"></i>
              <span class="text-xs font-semibold">ก่อนหน้า</span>
            </div>
          </button>

          <button
            @click="nextCharacter"
            :disabled="isLoading || currentIndex >= survivors.length - 1"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isLoading || currentIndex >= survivors.length - 1 }"
          >
            <div class="btn-content">
              <i class="fas fa-arrow-right text-base"></i>
              <span class="text-xs font-semibold">ถัดไป</span>
            </div>
          </button>
        </div>
      </div>

      <div v-if="error" class="text-red-400 text-sm mt-3 animate-fadeIn">{{ error }}</div>
    </div>

    <!-- Inventory Data Display -->
    <div
      v-if="inventoryData"
      class="bg-gray-700 bg-opacity-90 rounded-lg p-5 border border-gray-600 animate-scaleIn mt-6"
    >
      <h3 class="text-lg font-semibold text-indigo-300 mb-4 font-orbitron">ข้อมูลน้ำหนักไอเทม</h3>

      <!-- Storage Weight Comparison -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบน้ำหนักในคลัง</h4>

        <div class="text-xs">
          <p class="text-indigo-200">จำนวนไอเทมในคลัง: {{ storageItems.length }}</p>
          <p class="text-indigo-200">จำนวนไอเทมล็อคในคลัง: {{ lockedStorageItems.length }}</p>
          <p class="text-indigo-200">จำนวนไอเทมยังไม่ล็อคในคลัง: {{ unlockedStorageItems.length }}</p>
          <p class="text-indigo-200">
            จำนวน Invalid Item IDs: {{ invalidStorageItems.length }}
            (Undefined: {{ invalidStorageItems.filter(id => id === undefined).length }})
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">น้ำหนักในคลัง (จาก storages)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>น้ำหนัก: {{ inventoryData.storageWeight }}</p>
                <p>ความจุสูงสุด: {{ inventoryData.storageMaxLoad }}</p>
              </div>
            </div>

            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">น้ำหนักที่คำนวณได้ (จากไอเทมในคลัง)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>น้ำหนัก: {{ calculatedStorageWeight }}</p>
              </div>
            </div>
          </div>

          <p
            class="mt-3 text-sm"
            :class="isStorageWeightMatching ? 'text-green-400' : 'text-red-400'"
          >
            {{ isStorageWeightMatching ? 'น้ำหนักในคลังตรงกัน' : 'น้ำหนักในคลังไม่ตรงกัน' }}
          </p>

          <button
            v-if="!isStorageWeightMatching"
            @click="updateStorageWeight"
            :disabled="isUpdating"
            class="btn btn-primary mt-3 mr-2"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-sync-alt text-base"></i>
              <span class="text-xs font-semibold">อัปเดตน้ำหนักในคลัง</span>
            </div>
          </button>

          <button
            v-if="invalidStorageItems.length > 0"
            @click="removeInvalidStorageItems"
            :disabled="isUpdating"
            class="btn btn-danger mt-3"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-trash text-base"></i>
              <span class="text-xs font-semibold">ลบ Invalid Item IDs</span>
            </div>
          </button>
        </div>

        <!-- Invalid Storage Items -->
        <div v-if="invalidStorageItems.length > 0" class="mt-4">
          <h5 class="font-semibold text-indigo-300 text-sm mb-2">Invalid Item IDs ในคลัง</h5>
          <div class="text-indigo-200 text-xs space-y-2">
            <div
              v-for="(itemId, index) in invalidStorageItems"
              :key="'invalid-storage-' + index"
              class="flex justify-between p-2 bg-gray-700 rounded-md"
            >
              <span>{{ itemId === undefined ? 'undefined' : itemId }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Bag Weight Comparison -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบน้ำหนักในกระเป๋า</h4>

        <div class="text-xs">
          <p class="text-indigo-200">จำนวนไอเทมในกระเป๋า: {{ bagItems.length }}</p>
          <p class="text-indigo-200">จำนวนไอเทมล็อคในกระเป๋า: {{ lockedBagItems.length }}</p>
          <p class="text-indigo-200">จำนวนไอเทมยังไม่ล็อคในกระเป๋า: {{ unlockedBagItems.length }}</p>
          <p class="text-indigo-200">
            จำนวน Invalid Item IDs: {{ invalidBagItems.length }}
            (Undefined: {{ invalidBagItems.filter(id => id === undefined).length }})
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">น้ำหนักในกระเป๋า (จาก bags)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>น้ำหนัก: {{ inventoryData.currentWeight }}</p>
                <p>ความจุสูงสุด: {{ inventoryData.maxLoad }}</p>
              </div>
            </div>

            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">น้ำหนักที่คำนวณได้ (จากไอเทมในกระเป๋า)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>น้ำหนัก: {{ calculatedBagWeight }}</p>
              </div>
            </div>
          </div>

          <p
            class="mt-3 text-sm"
            :class="isBagWeightMatching ? 'text-green-400' : 'text-red-400'"
          >
            {{ isBagWeightMatching ? 'น้ำหนักในกระเป๋าตรงกัน' : 'น้ำหนักในกระเป๋าไม่ตรงกัน' }}
          </p>

          <button
            v-if="!isBagWeightMatching"
            @click="updateBagWeight"
            :disabled="isUpdating"
            class="btn btn-primary mt-3 mr-2"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-sync-alt text-base"></i>
              <span class="text-xs font-semibold">อัปเดตน้ำหนักในกระเป๋า</span>
            </div>
          </button>

          <button
            v-if="invalidBagItems.length > 0"
            @click="removeInvalidBagItems"
            :disabled="isUpdating"
            class="btn btn-danger mt-3"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-trash text-base"></i>
              <span class="text-xs font-semibold">ลบ Invalid Item IDs</span>
            </div>
          </button>
        </div>

        <!-- Invalid Bag Items -->
        <div v-if="invalidBagItems.length > 0" class="mt-4">
          <h5 class="font-semibold text-indigo-300 text-sm mb-2">Invalid Item IDs ในกระเป๋า</h5>
          <div class="text-indigo-200 text-xs space-y-2">
            <div
              v-for="(itemId, index) in invalidBagItems"
              :key="'invalid-bag-' + index"
              class="flex justify-between p-2 bg-gray-700 rounded-md"
            >
              <span>{{ itemId === undefined ? 'undefined' : itemId }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Item Lists -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">รายการไอเทม</h4>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <!-- Storage Items -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <div class="mb-3">
              <h5 class="font-semibold text-indigo-300 text-sm mb-2">
                ไอเทมในคลัง
              </h5>

              <div class="flex flex-wrap gap-2">
                <button
                  v-if="unlockedStorageItems.length > 0"
                  @click="lockAllStorageItems"
                  :disabled="isUpdating"
                  class="btn btn-primary py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                >
                  <div class="btn-content">
                    <i class="fas fa-lock text-sm"></i>
                    <span class="text-xs font-semibold">ล็อคคลังทั้งหมด</span>
                  </div>
                </button>

                <button
                  v-if="lockedStorageItems.length > 0"
                  @click="unlockAllStorageItems"
                  :disabled="isUpdating"
                  class="btn btn-primary py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                >
                  <div class="btn-content">
                    <i class="fas fa-unlock text-sm"></i>
                    <span class="text-xs font-semibold">ปลดล็อคคลังทั้งหมด</span>
                  </div>
                </button>

                <button
                  v-if="storageItems.length > 0"
                  @click="clearStorage"
                  :disabled="isUpdating"
                  class="btn btn-danger py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                >
                  <div class="btn-content">
                    <i class="fas fa-trash text-sm"></i>
                    <span class="text-xs font-semibold">เคลียร์คลังทั้งหมด</span>
                  </div>
                </button>
              </div>

              <!-- Select All Transferable Storage -->
              <div v-if="transferableStorageItems.length > 0" class="mt-2 flex flex-wrap items-center gap-2">
                <label class="flex items-center gap-1 text-xs text-indigo-300 cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="isAllStorageSelected"
                    @change="toggleSelectAllStorage($event.target.checked)"
                    :disabled="isUpdating || isSending"
                  />
                  เลือกทั้งหมด ({{ transferableStorageItems.length }} ชิ้น)
                </label>

                <button
                  v-if="selectedSendCount > 0"
                  @click="openSendModal"
                  :disabled="isUpdating || isSending"
                  class="btn btn-primary py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating || isSending }"
                >
                  <div class="btn-content">
                    <i class="fas fa-paper-plane text-sm"></i>
                    <span class="text-xs font-semibold">ย้ายไอเทมที่เลือก</span>
                  </div>
                </button>
              </div>
            </div>

            <div v-if="storageItems.length > 0" class="text-indigo-200 text-xs space-y-2">
              <div
                v-for="item in storageItems"
                :key="item.id"
                class="flex justify-between items-center gap-2 p-2 bg-gray-700 rounded-md"
              >
                <span class="flex items-center gap-2 min-w-0">
                  <input
                    v-if="item.transfer"
                    type="checkbox"
                    :value="`storage:${item.id}`"
                    v-model="selectedSendItems"
                    :disabled="isUpdating || isSending"
                    class="accent-indigo-500 cursor-pointer"
                  />
                  <button
                    @click="toggleItemLocked(item)"
                    :disabled="isUpdating"
                    class="transition-colors duration-200 disabled:opacity-50"
                    :class="item.locked === true ? 'text-red-500 hover:text-red-300' : 'text-green-400 hover:text-green-300'"
                    :title="item.locked === true ? 'กดเพื่อปลดล็อคไอเทม' : 'กดเพื่อล็อคไอเทม'"
                  >
                    <i :class="item.locked === true ? 'fas fa-lock' : 'fas fa-unlock'"></i>
                  </button>

                  <span class="truncate">{{ item.name || 'ไม่ทราบชื่อ' }}</span>
                </span>

                <span class="break-all">{{ item.id }}</span>
                <span class="whitespace-nowrap">น้ำหนัก: {{ item.weight || 0 }}</span>

                <label class="flex items-center gap-1 ml-2 whitespace-nowrap">
                  <input
                    type="checkbox"
                    :checked="item.transfer || false"
                    @change="updateTransfer(item, $event.target.checked)"
                    :disabled="isUpdating"
                  />
                  Transfer
                </label>

                <button
                  v-if="item.transfer"
                  @click="openSendModalForItem('storage', item)"
                  :disabled="isUpdating || isSending"
                  class="btn btn-primary ml-2 py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating || isSending }"
                >
                  <div class="btn-content">
                    <i class="fas fa-paper-plane text-sm"></i>
                    <span class="text-xs font-semibold">ย้าย</span>
                  </div>
                </button>

                <button
                  @click="removeStorageItem(item.id)"
                  :disabled="isUpdating"
                  class="btn btn-danger ml-2 py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                >
                  <div class="btn-content">
                    <i class="fas fa-trash text-sm"></i>
                    <span class="text-xs font-semibold">ลบ</span>
                  </div>
                </button>
              </div>
            </div>

            <div v-else class="text-indigo-200 text-xs">ไม่มีไอเทมในคลัง</div>
          </div>

          <!-- Bag Items -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <div class="mb-3">
              <h5 class="font-semibold text-indigo-300 text-sm mb-2">
                ไอเทมในกระเป๋า
              </h5>

              <div class="flex flex-wrap gap-2">
                <button
                  v-if="unlockedBagItems.length > 0"
                  @click="lockAllBagItems"
                  :disabled="isUpdating"
                  class="btn btn-primary py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                >
                  <div class="btn-content">
                    <i class="fas fa-lock text-sm"></i>
                    <span class="text-xs font-semibold">ล็อคกระเป๋าทั้งหมด</span>
                  </div>
                </button>

                <button
                  v-if="lockedBagItems.length > 0"
                  @click="unlockAllBagItems"
                  :disabled="isUpdating"
                  class="btn btn-primary py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                >
                  <div class="btn-content">
                    <i class="fas fa-unlock text-sm"></i>
                    <span class="text-xs font-semibold">ปลดล็อคกระเป๋าทั้งหมด</span>
                  </div>
                </button>

                <button
                  v-if="bagItems.length > 0"
                  @click="clearBag"
                  :disabled="isUpdating"
                  class="btn btn-danger py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                >
                  <div class="btn-content">
                    <i class="fas fa-trash text-sm"></i>
                    <span class="text-xs font-semibold">เคลียร์กระเป๋าทั้งหมด</span>
                  </div>
                </button>
              </div>

              <!-- Select All Transferable Bag -->
              <div v-if="transferableBagItems.length > 0" class="mt-2 flex flex-wrap items-center gap-2">
                <label class="flex items-center gap-1 text-xs text-indigo-300 cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="isAllBagSelected"
                    @change="toggleSelectAllBag($event.target.checked)"
                    :disabled="isUpdating || isSending"
                  />
                  เลือกทั้งหมด ({{ transferableBagItems.length }} ชิ้น)
                </label>

                <button
                  v-if="selectedSendCount > 0"
                  @click="openSendModal"
                  :disabled="isUpdating || isSending"
                  class="btn btn-primary py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating || isSending }"
                >
                  <div class="btn-content">
                    <i class="fas fa-paper-plane text-sm"></i>
                    <span class="text-xs font-semibold">ย้ายไอเทมที่เลือก</span>
                  </div>
                </button>
              </div>
            </div>

            <div v-if="bagItems.length > 0" class="text-indigo-200 text-xs space-y-2">
              <div
                v-for="item in bagItems"
                :key="item.id"
                class="flex justify-between items-center gap-2 p-2 bg-gray-700 rounded-md"
              >
                <span class="flex items-center gap-2 min-w-0">
                  <input
                    v-if="item.transfer"
                    type="checkbox"
                    :value="`bag:${item.id}`"
                    v-model="selectedSendItems"
                    :disabled="isUpdating || isSending"
                    class="accent-indigo-500 cursor-pointer"
                  />
                  <button
                    @click="toggleItemLocked(item)"
                    :disabled="isUpdating"
                    class="transition-colors duration-200 disabled:opacity-50"
                    :class="item.locked === true ? 'text-red-500 hover:text-red-300' : 'text-green-400 hover:text-green-300'"
                    :title="item.locked === true ? 'กดเพื่อปลดล็อคไอเทม' : 'กดเพื่อล็อคไอเทม'"
                  >
                    <i :class="item.locked === true ? 'fas fa-lock' : 'fas fa-unlock'"></i>
                  </button>

                  <span class="truncate">{{ item.name || 'ไม่ทราบชื่อ' }} (x{{ item.quantity }})</span>
                </span>

                <span class="break-all">{{ item.id }}</span>
                <span class="whitespace-nowrap">น้ำหนัก: {{ (item.weight || 0) * item.quantity }}</span>

                <label class="flex items-center gap-1 ml-2 whitespace-nowrap">
                  <input
                    type="checkbox"
                    :checked="item.transfer || false"
                    @change="updateTransfer(item, $event.target.checked)"
                    :disabled="isUpdating"
                  />
                  Transfer
                </label>

                <button
                  v-if="item.transfer"
                  @click="openSendModalForItem('bag', item)"
                  :disabled="isUpdating || isSending"
                  class="btn btn-primary ml-2 py-1 px-2 text-xs"
                  :class="{ 'cursor-not-allowed opacity-70': isUpdating || isSending }"
                >
                  <div class="btn-content">
                    <i class="fas fa-paper-plane text-sm"></i>
                    <span class="text-xs font-semibold">ย้าย</span>
                  </div>
                </button>

                <div class="flex gap-2">
                  <button
                    @click="removeBagItem(item.id, false)"
                    :disabled="isUpdating"
                    class="btn btn-danger ml-2 py-1 px-2 text-xs"
                    :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                  >
                    <div class="btn-content">
                      <i class="fas fa-trash text-sm"></i>
                      <span class="text-xs font-semibold">ลบ 1 ชิ้น</span>
                    </div>
                  </button>

                  <button
                    v-if="item.quantity > 1"
                    @click="removeBagItem(item.id, true)"
                    :disabled="isUpdating"
                    class="btn btn-danger ml-2 py-1 px-2 text-xs"
                    :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
                  >
                    <div class="btn-content">
                      <i class="fas fa-trash text-sm"></i>
                      <span class="text-xs font-semibold">ลบทั้งหมด</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="text-indigo-200 text-xs">ไม่มีไอเทมในกระเป๋า</div>

            <!-- ส่วนเพิ่มไอเทมใหม่ -->
            <div class="mt-6">
              <h5 class="font-semibold text-indigo-300 text-sm mb-3">เพิ่มไอเทมลงกระเป๋า</h5>

              <div class="flex flex-col sm:flex-row items-center gap-3">
                <input
                  v-model.trim="newItemId"
                  type="text"
                  placeholder="กรอก Item ID (เช่น TQzJntRKJTqUzVHARXS9)"
                  class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
                />

                <button
                  @click="addItemToBag"
                  :disabled="isLoading || isUpdating || !newItemId"
                  class="btn btn-primary w-full sm:w-auto"
                  :class="{ 'cursor-not-allowed opacity-70': isLoading || isUpdating || !newItemId }"
                >
                  <div class="btn-content">
                    <i class="fas fa-plus text-base"></i>
                    <span class="text-xs font-semibold">เพิ่มลงกระเป๋า</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Send Selected Items Button Bar -->
        <div
          v-if="selectedSendCount > 0"
          class="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-[9999] bg-gray-800 border border-indigo-500 rounded-lg px-4 py-3 shadow-xl flex items-center gap-4 animate-fadeIn"
        >
          <div class="text-indigo-200 text-sm">
            <span class="font-semibold">ส่งไอเทมที่เลือก</span>
            <span class="ml-2">({{ selectedSendCount }} ชิ้น, น้ำหนักรวม {{ selectedSendWeight }})</span>
          </div>
          <button
            @click="openSendModal"
            :disabled="isSending"
            class="btn btn-primary py-1 px-3 text-sm"
            :class="{ 'cursor-not-allowed opacity-70': isSending }"
          >
            <div class="btn-content">
              <i class="fas fa-paper-plane text-sm"></i>
              <span class="text-xs font-semibold">เปิดหน้าต่างส่ง</span>
            </div>
          </button>
          <button
            @click="selectedSendItems = []"
            :disabled="isSending"
            class="text-gray-400 hover:text-gray-200 text-sm"
            :class="{ 'cursor-not-allowed opacity-50': isSending }"
            title="ยกเลิกการเลือก"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Send Item Modal -->
    <div
      v-if="showSendModal"
      class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 px-4"
      @click.self="closeSendModal"
    >
      <div class="bg-gray-800 border border-gray-600 rounded-lg p-5 w-full max-w-md max-h-[80vh] overflow-auto animate-scaleIn">
        <h3 class="text-lg font-semibold text-indigo-300 mb-4 font-orbitron flex items-center gap-2">
          <i class="fas fa-paper-plane text-indigo-400"></i>
          ส่งไอเทมให้ผู้เล่น
        </h3>

        <!-- Selected Items Summary -->
        <div class="mb-4 max-h-48 overflow-auto bg-gray-700 rounded-md p-3 border border-gray-600">
          <p class="text-indigo-200 text-xs font-semibold mb-2">
            ไอเทมที่เลือก ({{ selectedSendCount }} ชิ้น, น้ำหนักรวม {{ selectedSendWeight }})
          </p>
          <div class="space-y-1">
            <div
              v-for="{ item, source } in selectedSendList"
              :key="item.id"
              class="flex justify-between text-xs text-indigo-200"
            >
              <span class="truncate">
                <span
                  class="inline-block w-2 h-2 rounded-full mr-1"
                  :class="source === 'storage' ? 'bg-amber-400' : 'bg-emerald-400'"
                ></span>
                {{ item.name || 'ไม่ทราบชื่อ' }}
                <span class="text-gray-400">(x{{ item.quantity || 1 }})</span>
              </span>
              <span class="whitespace-nowrap ml-2">{{ (item.weight || 0) * (item.quantity || 1) }}</span>
            </div>
          </div>
        </div>

        <!-- Recipient Input -->
        <div class="mb-4">
          <label class="block text-indigo-300 text-sm font-semibold mb-2">UID ผู้รับ</label>
          <input
            v-model.trim="recipientUid"
            type="text"
            placeholder="กรอก UID ผู้เล่นที่จะรับไอเทม"
            class="w-full p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
            :disabled="isSending"
          />
          <p v-if="error && showSendModal" class="text-red-400 text-xs mt-2">{{ error }}</p>
        </div>

        <!-- Actions -->
        <div class="flex gap-3 justify-end">
          <button
            @click="closeSendModal"
            :disabled="isSending"
            class="btn btn-secondary py-1 px-3 text-sm"
            :class="{ 'cursor-not-allowed opacity-70': isSending }"
          >
            <div class="btn-content">
              <span class="text-xs font-semibold">ยกเลิก</span>
            </div>
          </button>
          <button
            @click="sendItems"
            :disabled="isSending || !recipientUid"
            class="btn btn-primary py-1 px-3 text-sm"
            :class="{ 'cursor-not-allowed opacity-70': isSending || !recipientUid }"
          >
            <div class="btn-content">
              <i v-if="isSending" class="fas fa-spinner fa-spin text-sm"></i>
              <i v-else class="fas fa-check text-sm"></i>
              <span class="text-xs font-semibold">{{ isSending ? 'กำลังส่ง...' : 'ยืนยันการส่ง' }}</span>
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- No Data -->
    <div
      v-else-if="!isLoading && !inventoryData"
      class="text-center text-indigo-300 text-sm animate-fadeIn"
    >
      กรุณากรอก UID หรือเลือกตัวละครเพื่อตรวจสอบข้อมูล
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { projectFirestore, projectFunctions, FieldValue } from '../firebase/config';
import gameItemsData from '@/data/gameItems.json';

export default {
  setup() {
    const uid = ref('');
    const selectedCharacter = ref('');
    const inventoryData = ref(null);
    const storageItems = ref([]);
    const bagItems = ref([]);
    const invalidStorageItems = ref([]);
    const invalidBagItems = ref([]);
    const survivors = ref([]);
    const error = ref(null);
    const isLoading = ref(false);
    const isUpdating = ref(false);
    const currentIndex = ref(-1);
    const pageSize = 150;
    const lastDoc = ref(null);
    const hasMoreData = ref(true);

    // สำหรับเพิ่มไอเทม
    const newItemId = ref('');

    // สำหรับส่งไอเทมให้ผู้เล่นอื่น
    const selectedSendItems = ref([]);
    const showSendModal = ref(false);
    const recipientUid = ref('');
    const isSending = ref(false);

    const lockedStorageItems = computed(() => {
      return storageItems.value.filter(item => item.locked === true);
    });

    const unlockedStorageItems = computed(() => {
      return storageItems.value.filter(item => item.locked !== true);
    });

    const lockedBagItems = computed(() => {
      return bagItems.value.filter(item => item.locked === true);
    });

    const unlockedBagItems = computed(() => {
      return bagItems.value.filter(item => item.locked !== true);
    });

    const transferableStorageItems = computed(() => {
      return storageItems.value.filter(item => item.transfer === true);
    });

    const transferableBagItems = computed(() => {
      return bagItems.value.filter(item => item.transfer === true);
    });

    const selectedSendList = computed(() => {
      return selectedSendItems.value.map(key => {
        const [source, id] = key.split(':');
        const items = source === 'storage' ? storageItems.value : bagItems.value;
        const item = items.find(i => i.id === id);
        return item ? { source, id, item } : null;
      }).filter(Boolean);
    });

    const selectedSendCount = computed(() => selectedSendList.value.length);

    const selectedSendWeight = computed(() => {
      return selectedSendList.value.reduce((sum, { item }) => {
        return sum + ((item.weight || 0) * (item.quantity || 1));
      }, 0);
    });

    const calculatedStorageWeight = computed(() => {
      return storageItems.value.reduce((sum, item) => sum + (item.weight || 0), 0);
    });

    const calculatedBagWeight = computed(() => {
      return bagItems.value.reduce((sum, item) => sum + (item.weight || 0) * (item.quantity || 1), 0);
    });

    const isStorageWeightMatching = computed(() => {
      if (!inventoryData.value || !inventoryData.value.storageWeight) return true;
      return inventoryData.value.storageWeight === calculatedStorageWeight.value;
    });

    const isBagWeightMatching = computed(() => {
      if (!inventoryData.value || !inventoryData.value.currentWeight) return true;
      return inventoryData.value.currentWeight === calculatedBagWeight.value;
    });

    const fetchSurvivors = async () => {
      try {
        isLoading.value = true;

        const query = projectFirestore
          .collection('characters')
          .where('rank', '==', true)
          .where('isLive', '==', true)
          .orderBy('survivalStartTime', 'asc')
          .orderBy('room', 'asc')
          .limit(pageSize);

        const snapshot = await query.get();

        survivors.value = snapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));

        lastDoc.value = snapshot.docs[snapshot.docs.length - 1];
        hasMoreData.value = snapshot.docs.length === pageSize;
        isLoading.value = false;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดอันดับ: ' + err.message;
        isLoading.value = false;
      }
    };

    const fetchInventoryData = async () => {
      if (!uid.value) {
        error.value = 'กรุณากรอก UID';
        return;
      }

      selectedCharacter.value = '';
      currentIndex.value = survivors.value.findIndex((s) => s.docId === uid.value);
      isLoading.value = true;
      error.value = null;
      inventoryData.value = null;
      storageItems.value = [];
      bagItems.value = [];
      invalidStorageItems.value = [];
      invalidBagItems.value = [];
      selectedSendItems.value = [];

      try {
        const storageDoc = await projectFirestore
          .collection('storages')
          .doc(uid.value)
          .get();

        let storageWeight = 0;
        let storageMaxLoad = 20;

        if (storageDoc.exists) {
          const storageData = storageDoc.data();
          storageWeight = storageData.currentWeight || 0;
          storageMaxLoad = storageData.maxLoad || 20;
          const itemsObj = storageData.items || {};

          if (typeof itemsObj === 'object' && Object.keys(itemsObj).length > 0) {
            const mappedItems = Object.entries(itemsObj).map(([instanceId, itemData]) => {
              const states = itemData.states || {};
              const template = gameItemsData[itemData.templateId] || {};

              return {
                id: instanceId,
                templateId: itemData.templateId,
                name: template.name || itemData.templateId,
                weight: template.weight || 0,
                transfer: states.transfer || false,
                locked: states.locked === true,
                ...template,
                ...states,
              };
            });

            storageItems.value = mappedItems;

            invalidStorageItems.value = mappedItems
              .filter(item => !gameItemsData[item.templateId])
              .map(item => item.id);
          }
        }

        const bagDoc = await projectFirestore
          .collection('bags')
          .doc(uid.value)
          .get();

        let currentWeight = 0;
        let maxLoad = 20;

        if (bagDoc.exists) {
          const bagData = bagDoc.data();
          currentWeight = bagData.currentWeight || 0;
          maxLoad = bagData.maxLoad || 20;
          const itemsObj = bagData.items || {};

          if (typeof itemsObj === 'object' && Object.keys(itemsObj).length > 0) {
            const mappedItems = Object.entries(itemsObj).map(([instanceId, itemData]) => {
              const states = itemData.states || {};
              const template = gameItemsData[itemData.templateId] || {};

              return {
                id: instanceId,
                templateId: itemData.templateId,
                name: template.name || itemData.templateId,
                weight: template.weight || 0,
                quantity: states.quantity || 1,
                transfer: states.transfer || false,
                locked: states.locked === true,
                ...template,
                ...states,
              };
            });

            bagItems.value = mappedItems;

            invalidBagItems.value = mappedItems
              .filter(item => !gameItemsData[item.templateId])
              .map(item => item.id);
          }
        }

        inventoryData.value = {
          storageWeight,
          storageMaxLoad,
          currentWeight,
          maxLoad,
        };
      } catch (err) {
        console.error('Error fetching inventory data:', err);
        error.value = `เกิดข้อผิดพลาด: ${err.message}`;
      } finally {
        isLoading.value = false;
      }
    };

    const fetchInventoryDataByCharacter = async () => {
      if (!selectedCharacter.value) {
        inventoryData.value = null;
        storageItems.value = [];
        bagItems.value = [];
        invalidStorageItems.value = [];
        invalidBagItems.value = [];
        selectedSendItems.value = [];
        uid.value = '';
        currentIndex.value = -1;
        return;
      }

      uid.value = selectedCharacter.value;
      currentIndex.value = survivors.value.findIndex((s) => s.docId === selectedCharacter.value);
      await fetchInventoryData();
    };

    const previousCharacter = async () => {
      if (currentIndex.value > 0) {
        currentIndex.value--;
        selectedCharacter.value = survivors.value[currentIndex.value].docId;
        uid.value = selectedCharacter.value;
        await fetchInventoryData();
      }
    };

    const nextCharacter = async () => {
      if (currentIndex.value < survivors.value.length - 1) {
        currentIndex.value++;
        selectedCharacter.value = survivors.value[currentIndex.value].docId;
        uid.value = selectedCharacter.value;
        await fetchInventoryData();
      }
    };

    const updateStorageWeight = async () => {
      if (!uid.value || !inventoryData.value) return;

      isUpdating.value = true;
      error.value = null;

      try {
        const oldWeight = inventoryData.value.storageWeight;

        await projectFirestore
          .collection('storages')
          .doc(uid.value)
          .update({
            currentWeight: calculatedStorageWeight.value,
            updatedAt: new Date(),
          });

        inventoryData.value.storageWeight = calculatedStorageWeight.value;

        // log removed
      } catch (err) {
        console.error('Error updating storage weight:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดตน้ำหนักในคลัง: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const updateBagWeight = async () => {
      if (!uid.value || !inventoryData.value) return;

      isUpdating.value = true;
      error.value = null;

      try {
        const oldWeight = inventoryData.value.currentWeight;

        await projectFirestore
          .collection('bags')
          .doc(uid.value)
          .update({
            currentWeight: calculatedBagWeight.value,
            updatedAt: new Date(),
          });

        inventoryData.value.currentWeight = calculatedBagWeight.value;

        // log removed
      } catch (err) {
        console.error('Error updating bag weight:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดตน้ำหนักในกระเป๋า: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const removeInvalidStorageItems = async () => {
      if (!uid.value || invalidStorageItems.value.length === 0) return;

      isUpdating.value = true;
      error.value = null;

      try {
        const storageDocRef = projectFirestore.collection('storages').doc(uid.value);
        const storageDoc = await storageDocRef.get();

        if (storageDoc.exists) {
          const updates = {};

          invalidStorageItems.value.forEach(id => {
            updates[`items.${id}`] = FieldValue.delete();
          });

          updates.currentWeight = calculatedStorageWeight.value;
          updates.updatedAt = new Date();

          await storageDocRef.update(updates);

          // log removed

          invalidStorageItems.value = [];
          await fetchInventoryData();
        }
      } catch (err) {
        console.error('Error removing invalid storage items:', err);
        error.value = `เกิดข้อผิดพลาดในการลบ Invalid Item IDs ในคลัง: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const removeInvalidBagItems = async () => {
      if (!uid.value || invalidBagItems.value.length === 0) return;

      isUpdating.value = true;
      error.value = null;

      try {
        const bagDocRef = projectFirestore.collection('bags').doc(uid.value);
        const bagDoc = await bagDocRef.get();

        if (bagDoc.exists) {
          const updates = {};

          invalidBagItems.value.forEach(id => {
            updates[`items.${id}`] = FieldValue.delete();
          });

          updates.currentWeight = calculatedBagWeight.value;
          updates.updatedAt = new Date();

          await bagDocRef.update(updates);

          // log removed

          invalidBagItems.value = [];
          await fetchInventoryData();
        }
      } catch (err) {
        console.error('Error removing invalid bag items:', err);
        error.value = `เกิดข้อผิดพลาดในการลบ Invalid Item IDs ในกระเป๋า: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const removeStorageItem = async (itemId) => {
      if (!uid.value || !itemId) return;

      isUpdating.value = true;
      error.value = null;

      try {
        const storageDocRef = projectFirestore.collection('storages').doc(uid.value);
        const storageDoc = await storageDocRef.get();

        if (storageDoc.exists) {
          const storageData = storageDoc.data();
          const removedWeight = storageItems.value.find((item) => item.id === itemId)?.weight || 0;
          const newWeight = Math.max((storageData.currentWeight || 0) - removedWeight, 0);

          await storageDocRef.update({
            [`items.${itemId}`]: FieldValue.delete(),
            currentWeight: newWeight,
            updatedAt: new Date(),
          });

          // log removed

          await fetchInventoryData();
        }
      } catch (err) {
        console.error('Error removing storage item:', err);
        error.value = `เกิดข้อผิดพลาดในการลบไอเทมจากคลัง: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const removeBagItem = async (itemId, removeAll = false) => {
      if (!uid.value || !itemId) return;

      isUpdating.value = true;
      error.value = null;

      try {
        const bagDocRef = projectFirestore.collection('bags').doc(uid.value);
        const bagDoc = await bagDocRef.get();

        if (bagDoc.exists) {
          const bagData = bagDoc.data();
          const itemRef = bagData.items?.[itemId];
          const itemWeight = bagItems.value.find((item) => item.id === itemId)?.weight || 0;

          let removedCount = 0;
          let removedWeight = 0;

          if (removeAll) {
            removedCount = itemRef?.states?.quantity || 1;
            removedWeight = itemWeight * removedCount;

            await bagDocRef.update({
              [`items.${itemId}`]: FieldValue.delete(),
              currentWeight: Math.max((bagData.currentWeight || 0) - removedWeight, 0),
              updatedAt: new Date(),
            });
          } else {
            const currentQuantity = itemRef?.states?.quantity || 1;

            if (currentQuantity > 1) {
              removedCount = 1;
              removedWeight = itemWeight;

              await bagDocRef.update({
                [`items.${itemId}.states.quantity`]: currentQuantity - 1,
                currentWeight: Math.max((bagData.currentWeight || 0) - itemWeight, 0),
                updatedAt: new Date(),
              });
            } else {
              removedCount = 1;
              removedWeight = itemWeight;

              await bagDocRef.update({
                [`items.${itemId}`]: FieldValue.delete(),
                currentWeight: Math.max((bagData.currentWeight || 0) - itemWeight, 0),
                updatedAt: new Date(),
              });
            }
          }

          // log removed

          await fetchInventoryData();
        }
      } catch (err) {
        console.error('Error removing bag item:', err);
        error.value = `เกิดข้อผิดพลาดในการลบไอเทมจากกระเป๋า: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const updateTransfer = async (item, newValue) => {
      if (!uid.value || !item?.id) return;

      isUpdating.value = true;
      error.value = null;

      const oldValue = item.transfer || false;
      item.transfer = newValue;

      try {
        const inStorage = storageItems.value.some(i => i.id === item.id);
        const inBag = bagItems.value.some(i => i.id === item.id);

        if (inStorage) {
          await projectFirestore.collection('storages').doc(uid.value).update({
            [`items.${item.id}.states.transfer`]: newValue,
            updatedAt: new Date(),
          });
        } else if (inBag) {
          await projectFirestore.collection('bags').doc(uid.value).update({
            [`items.${item.id}.states.transfer`]: newValue,
            updatedAt: new Date(),
          });
        }

        // log removed
      } catch (err) {
        console.error('Error updating transfer:', err);
        error.value = `เกิดข้อผิดพลาดในการปรับ transfer: ${err.message}`;
        item.transfer = oldValue;
      } finally {
        isUpdating.value = false;
      }
    };

    const toggleItemLocked = async (item) => {
      if (!uid.value || !item?.id) return;

      const oldLocked = item.locked === true;
      const newLocked = !oldLocked;

      const confirmed = confirm(
        `คุณต้องการ${newLocked ? 'ล็อค' : 'ปลดล็อค'}ไอเทม "${item.name || item.id}" ใช่หรือไม่?`
      );

      if (!confirmed) return;

      isUpdating.value = true;
      error.value = null;
      item.locked = newLocked;

      try {
        const inStorage = storageItems.value.some(i => i.id === item.id);
        const inBag = bagItems.value.some(i => i.id === item.id);

        if (inStorage) {
          await projectFirestore.collection('storages').doc(uid.value).update({
            [`items.${item.id}.states.locked`]: newLocked,
            updatedAt: new Date(),
          });
        } else if (inBag) {
          await projectFirestore.collection('bags').doc(uid.value).update({
            [`items.${item.id}.states.locked`]: newLocked,
            updatedAt: new Date(),
          });
        }

        // log removed

        await fetchInventoryData();
      } catch (err) {
        console.error('Error toggling item locked:', err);
        error.value = `เกิดข้อผิดพลาดในการปรับสถานะล็อคไอเทม: ${err.message}`;
        item.locked = oldLocked;
      } finally {
        isUpdating.value = false;
      }
    };

    const setAllStorageItemsLocked = async (lockedValue) => {
      if (!uid.value || storageItems.value.length === 0) return;

      const targetItems = lockedValue ? unlockedStorageItems.value : lockedStorageItems.value;
      if (targetItems.length === 0) return;

      const confirmed = confirm(
        `คุณต้องการ${lockedValue ? 'ล็อค' : 'ปลดล็อค'}ไอเทมในคลังทั้งหมด ${targetItems.length} รายการ ใช่หรือไม่?`
      );

      if (!confirmed) return;

      isUpdating.value = true;
      error.value = null;

      const itemsToUpdate = targetItems.map(item => ({
        id: item.id,
        templateId: item.templateId || null,
        name: item.name || 'ไม่ทราบชื่อ',
      }));

      try {
        const updates = {};

        itemsToUpdate.forEach(item => {
          updates[`items.${item.id}.states.locked`] = lockedValue;
        });

        updates.updatedAt = new Date();

        await projectFirestore.collection('storages').doc(uid.value).update(updates);

        // log removed

        await fetchInventoryData();
      } catch (err) {
        console.error('Error updating all storage locked states:', err);
        error.value = `เกิดข้อผิดพลาดในการ${lockedValue ? 'ล็อค' : 'ปลดล็อค'}ไอเทมในคลังทั้งหมด: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const setAllBagItemsLocked = async (lockedValue) => {
      if (!uid.value || bagItems.value.length === 0) return;

      const targetItems = lockedValue ? unlockedBagItems.value : lockedBagItems.value;
      if (targetItems.length === 0) return;

      const confirmed = confirm(
        `คุณต้องการ${lockedValue ? 'ล็อค' : 'ปลดล็อค'}ไอเทมในกระเป๋าทั้งหมด ${targetItems.length} รายการ ใช่หรือไม่?`
      );

      if (!confirmed) return;

      isUpdating.value = true;
      error.value = null;

      const itemsToUpdate = targetItems.map(item => ({
        id: item.id,
        templateId: item.templateId || null,
        name: item.name || 'ไม่ทราบชื่อ',
      }));

      try {
        const updates = {};

        itemsToUpdate.forEach(item => {
          updates[`items.${item.id}.states.locked`] = lockedValue;
        });

        updates.updatedAt = new Date();

        await projectFirestore.collection('bags').doc(uid.value).update(updates);

        // log removed

        await fetchInventoryData();
      } catch (err) {
        console.error('Error updating all bag locked states:', err);
        error.value = `เกิดข้อผิดพลาดในการ${lockedValue ? 'ล็อค' : 'ปลดล็อค'}ไอเทมในกระเป๋าทั้งหมด: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const lockAllStorageItems = async () => {
      await setAllStorageItemsLocked(true);
    };

    const unlockAllStorageItems = async () => {
      await setAllStorageItemsLocked(false);
    };

    const lockAllBagItems = async () => {
      await setAllBagItemsLocked(true);
    };

    const unlockAllBagItems = async () => {
      await setAllBagItemsLocked(false);
    };

    const clearStorage = async () => {
      if (!uid.value || storageItems.value.length === 0) return;
      if (!confirm('คุณแน่ใจหรือไม่ที่จะเคลียร์คลังทั้งหมด? การกระทำนี้ไม่สามารถย้อนกลับได้')) return;

      isUpdating.value = true;
      error.value = null;

      try {
        const storageDocRef = projectFirestore.collection('storages').doc(uid.value);
        const storageDoc = await storageDocRef.get();

        if (storageDoc.exists) {
          const oldItems = storageDoc.data().items || {};
          const oldWeight = storageDoc.data().currentWeight || 0;

          await storageDocRef.update({
            items: {},
            currentWeight: 0,
            updatedAt: new Date(),
          });

          // log removed

          await fetchInventoryData();
        }
      } catch (err) {
        console.error('Error clearing storage:', err);
        error.value = `เกิดข้อผิดพลาดในการเคลียร์คลัง: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    const clearBag = async () => {
      if (!uid.value || bagItems.value.length === 0) return;
      if (!confirm('คุณแน่ใจหรือไม่ที่จะเคลียร์กระเป๋าทั้งหมด? การกระทำนี้ไม่สามารถย้อนกลับได้')) return;

      isUpdating.value = true;
      error.value = null;

      try {
        const bagDocRef = projectFirestore.collection('bags').doc(uid.value);
        const bagDoc = await bagDocRef.get();

        if (bagDoc.exists) {
          const oldItems = bagDoc.data().items || {};
          const oldWeight = bagDoc.data().currentWeight || 0;

          await bagDocRef.update({
            items: {},
            currentWeight: 0,
            updatedAt: new Date(),
          });

          // log removed

          await fetchInventoryData();
        }
      } catch (err) {
        console.error('Error clearing bag:', err);
        error.value = `เกิดข้อผิดพลาดในการเคลียร์กระเป๋า: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    // --- ฟังก์ชันส่งไอเทมให้ผู้เล่นอื่น ---
    const toggleSelectAllStorage = (checked) => {
      const keys = transferableStorageItems.value.map(item => `storage:${item.id}`);
      if (checked) {
        selectedSendItems.value = [...new Set([...selectedSendItems.value, ...keys])];
      } else {
        selectedSendItems.value = selectedSendItems.value.filter(k => !keys.includes(k));
      }
    };

    const toggleSelectAllBag = (checked) => {
      const keys = transferableBagItems.value.map(item => `bag:${item.id}`);
      if (checked) {
        selectedSendItems.value = [...new Set([...selectedSendItems.value, ...keys])];
      } else {
        selectedSendItems.value = selectedSendItems.value.filter(k => !keys.includes(k));
      }
    };

    const isAllStorageSelected = computed(() => {
      if (transferableStorageItems.value.length === 0) return false;
      return transferableStorageItems.value.every(item => selectedSendItems.value.includes(`storage:${item.id}`));
    });

    const isAllBagSelected = computed(() => {
      if (transferableBagItems.value.length === 0) return false;
      return transferableBagItems.value.every(item => selectedSendItems.value.includes(`bag:${item.id}`));
    });

    const openSendModal = () => {
      if (selectedSendCount.value === 0) return;
      recipientUid.value = '';
      showSendModal.value = true;
    };

    const openSendModalForItem = (source, item) => {
      if (!item?.id || item.transfer !== true) return;
      selectedSendItems.value = [`${source}:${item.id}`];
      recipientUid.value = '';
      showSendModal.value = true;
    };

    const closeSendModal = () => {
      showSendModal.value = false;
      recipientUid.value = '';
    };

    const sendItems = async () => {
      if (!recipientUid.value || selectedSendCount.value === 0) return;
      if (recipientUid.value === uid.value) {
        error.value = 'ไม่สามารถส่งไอเทมให้ตัวเองได้';
        return;
      }

      isSending.value = true;
      error.value = null;

      try {
        const db = projectFirestore;
        const recipientBagRef = db.collection('bags').doc(recipientUid.value);
        const recipientBagDoc = await recipientBagRef.get();

        if (!recipientBagDoc.exists) {
          throw new Error('ไม่พบกระเป๋าของผู้รับ (UID ไม่ถูกต้องหรือผู้เล่นไม่มีกระเป๋า)');
        }

        const recipientData = recipientBagDoc.data();
        let recipientWeight = recipientData.currentWeight || 0;
        const recipientMaxLoad = recipientData.maxLoad || 100;
        const recipientItems = recipientData.items || {};

        if (selectedSendWeight.value > (recipientMaxLoad - recipientWeight)) {
          throw new Error(
            `กระเป๋าของผู้รับน้ำหนักเกินขีดจำกัด (เหลือพื้นที่ ${recipientMaxLoad - recipientWeight}, ต้องการ ${selectedSendWeight.value})`
          );
        }

        // Read sender docs
        const senderStorageRef = db.collection('storages').doc(uid.value);
        const senderBagRef = db.collection('bags').doc(uid.value);
        const [senderStorageDoc, senderBagDoc] = await Promise.all([
          senderStorageRef.get(),
          senderBagRef.get(),
        ]);

        const senderStorageData = senderStorageDoc.exists ? senderStorageDoc.data() : {};
        const senderBagData = senderBagDoc.exists ? senderBagDoc.data() : {};

        const batch = db.batch();
        let newSenderStorageWeight = senderStorageData.currentWeight || 0;
        let newSenderBagWeight = senderBagData.currentWeight || 0;
        let newRecipientWeight = recipientWeight;
        const newRecipientItems = { ...recipientItems };

        for (const { source, id, item } of selectedSendList.value) {
          const rawItem = source === 'storage'
            ? senderStorageData.items?.[id]
            : senderBagData.items?.[id];

          if (!rawItem) {
            throw new Error(`ไม่พบไอเทม "${item.name || id}" ใน ${source === 'storage' ? 'คลัง' : 'กระเป๋า'} ของผู้ส่ง (อาจถูกย้ายไปแล้ว)`);
          }

          const itemWeight = (item.weight || 0) * (item.quantity || 1);
          newRecipientWeight += itemWeight;

          // Add to recipient
          newRecipientItems[id] = rawItem;

          // Remove from source
          if (source === 'storage') {
            batch.update(senderStorageRef, {
              [`items.${id}`]: FieldValue.delete(),
            });
            newSenderStorageWeight -= itemWeight;
          } else {
            batch.update(senderBagRef, {
              [`items.${id}`]: FieldValue.delete(),
            });
            newSenderBagWeight -= itemWeight;
          }
        }

        // Update recipient bag
        batch.update(recipientBagRef, {
          items: newRecipientItems,
          currentWeight: newRecipientWeight,
          updatedAt: new Date(),
        });

        // Update sender storage
        if (senderStorageDoc.exists && newSenderStorageWeight !== (senderStorageData.currentWeight || 0)) {
          batch.update(senderStorageRef, {
            currentWeight: Math.max(0, newSenderStorageWeight),
            updatedAt: new Date(),
          });
        }

        // Update sender bag
        if (senderBagDoc.exists && newSenderBagWeight !== (senderBagData.currentWeight || 0)) {
          batch.update(senderBagRef, {
            currentWeight: Math.max(0, newSenderBagWeight),
            updatedAt: new Date(),
          });
        }

        await batch.commit();

        // log removed

        selectedSendItems.value = [];
        showSendModal.value = false;
        recipientUid.value = '';
        await fetchInventoryData();
      } catch (err) {
        console.error('Error sending items:', err);
        error.value = `เกิดข้อผิดพลาดในการส่งไอเทม: ${err.message}`;
      } finally {
        isSending.value = false;
      }
    };

    // ฟังก์ชันเพิ่มไอเทมลงกระเป๋า (V2: ใช้ templateId แทน itemId)
    const addItemToBag = async () => {
      if (!uid.value) {
        error.value = 'กรุณาโหลดข้อมูลผู้เล่นก่อน';
        return;
      }

      const templateId = newItemId.value.trim();
      if (!templateId) return;

      isUpdating.value = true;
      error.value = null;

      try {
        const template = gameItemsData[templateId];

        if (!template) {
          error.value = `ไม่พบไอเทมที่มี Template ID: ${templateId}`;
          return;
        }

        const addWeight = template.weight || 0;

        const bagRef = projectFirestore.collection('bags').doc(uid.value);
        const bagSnap = await bagRef.get();

        let currentWeight = 0;
        let maxLoad = 20;
        let existingItems = {};

        if (bagSnap.exists) {
          const bagData = bagSnap.data();
          currentWeight = bagData.currentWeight || 0;
          maxLoad = bagData.maxLoad || 20;
          existingItems = bagData.items || {};
        }

        const instanceId = Date.now().toString(36) + Math.random().toString(36).substr(2, 9);

        await bagRef.set({
          items: {
            ...existingItems,
            [instanceId]: {
              templateId: templateId,
              states: {
                quantity: 1,
              },
            },
          },
          currentWeight: currentWeight + addWeight,
          maxLoad,
          updatedAt: new Date(),
        }, { merge: true });

        // log removed

        newItemId.value = '';
        await fetchInventoryData();
      } catch (err) {
        console.error('Error adding item to bag:', err);
        error.value = `เกิดข้อผิดพลาดในการเพิ่มไอเทม: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };

    onMounted(async () => {
      await fetchSurvivors();
    });

    return {
      uid,
      selectedCharacter,
      inventoryData,
      storageItems,
      bagItems,
      invalidStorageItems,
      invalidBagItems,
      survivors,
      error,
      isLoading,
      isUpdating,
      currentIndex,

      calculatedStorageWeight,
      isStorageWeightMatching,
      calculatedBagWeight,
      isBagWeightMatching,

      lockedStorageItems,
      unlockedStorageItems,
      lockedBagItems,
      unlockedBagItems,

      newItemId,
      addItemToBag,

      // Send item refs
      selectedSendItems,
      showSendModal,
      recipientUid,
      isSending,
      transferableStorageItems,
      transferableBagItems,
      selectedSendList,
      selectedSendCount,
      selectedSendWeight,
      isAllStorageSelected,
      isAllBagSelected,
      toggleSelectAllStorage,
      toggleSelectAllBag,
      openSendModal,
      openSendModalForItem,
      closeSendModal,
      sendItems,

      fetchInventoryData,
      fetchInventoryDataByCharacter,
      previousCharacter,
      nextCharacter,

      updateStorageWeight,
      updateBagWeight,

      removeInvalidStorageItems,
      removeInvalidBagItems,
      removeStorageItem,
      removeBagItem,

      updateTransfer,
      toggleItemLocked,

      lockAllStorageItems,
      unlockAllStorageItems,
      lockAllBagItems,
      unlockAllBagItems,

      clearStorage,
      clearBag,
      hasMoreData,
    };
  },
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1')
      no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

.font-orbitron {
  font-family: 'Orbitron', sans-serif;
}

.btn-secondary {
  @apply bg-gray-700 hover:bg-gray-600 text-white py-1 px-3 rounded-md text-sm border border-gray-600 transition-colors duration-200 flex justify-center items-center;
}

@media (max-width: 639px) {
  .text-lg {
    font-size: 1rem;
  }

  .text-xl {
    font-size: 1.125rem;
  }

  .text-sm {
    font-size: 0.75rem;
  }

  .p-5 {
    padding: 0.75rem;
  }
}
</style>